import { HOST_ELEMENT_KEY } from './renderer';

/**
 * Adding to {@link Child} decorator to a property allows you quickly
 * retrieve elements within the Directive or Component. The decorator
 * takes a CSS selector as it's first string, which should match the element
 * you're looking for.
 *
 * In the example below we're assigning the Component's `<div class="letters"></div>`
 * element to our `letterWrapper` property.
 *
 * ```typescript
 * @DomComponent({
 *  selector: 'aa-alphabet',
 *  template: `<div class="letters"><div>`
 * })
 * class Alphabet implements OnConnect {
 *  @Child('.letters')
 *  private readonly letterWrapper: HTMLElement;
 * }
 * ```
 *
 * This allows us to know get access to the element by referencing the property:
 * ```typescript
 *   setLetter(letter: string) {
 *     this.letterWrapper.innerText = letter;
 *   }
 * ```
 * *
 * @param selector
 */
export const Child = (selector: string): PropertyDecorator => {
  return function (target: any, propertyKey: string | symbol) {
    Object.defineProperty(target, propertyKey, {
      configurable: false,
      get: function () {
        const hostElm = this[HOST_ELEMENT_KEY]();
        return hostElm.querySelector.apply(hostElm, [selector]);
      },
    });
  };
};

/**
 * Practically the same as {@see Child}, but returns a list of elements instead
 * of one.
 *
 * @param selector
 */
export const Children = (selector: string): PropertyDecorator => {
  return function (target: any, propertyKey: string | symbol) {
    Object.defineProperty(target, propertyKey, {
      configurable: false,
      get: function () {
        const hostElm = this[HOST_ELEMENT_KEY]();
        return hostElm.querySelectorAll.apply(hostElm, [selector]);
      },
    });
  };
};
